import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from '../routing';
import { authApi } from '../utils/api';
import PasswordInput from '../components/PasswordInput';
import * as S from './authStyles';

// /reset-password/:token landing page. Validates the token on mount so
// expired / used links render the error state instead of a usable form.
export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [validating, setValidating] = useState(true);
  const [validError,  setValidError]  = useState(null);
  const [password,   setPassword]   = useState('');
  const [confirm,    setConfirm]    = useState('');
  const [error,      setError]      = useState('');
  const [saving,     setSaving]     = useState(false);
  const [done,       setDone]       = useState(false);

  useEffect(() => {
    authApi.validateResetToken(token)
      .then(() => setValidating(false))
      .catch(err => {
        setValidError(err.response?.data?.error || 'הקישור אינו תקין');
        setValidating(false);
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Trim so a stored password matches a trimmed login attempt — and users
    // can't accidentally bake stray spaces into their new password.
    const pw = password.trim();
    if (pw.length < 6) { setError('הסיסמה חייבת להכיל לפחות 6 תווים'); return; }
    if (pw !== confirm.trim()) { setError('הסיסמאות אינן תואמות'); return; }
    setSaving(true);
    try {
      await authApi.resetPassword(token, pw);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'לא הצלחנו לעדכן את הסיסמה — נסו שוב');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.container}>
      <div style={S.card}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: S.colors.primary }}>הגדרת סיסמה חדשה</div>
        </div>

        {validating ? (
          <div style={{ textAlign: 'center', color: S.colors.muted }}>בודקים את הקישור…</div>
        ) : validError ? (
          <>
            <div role="alert" style={S.errorBox}>
              {validError}
            </div>
            <Link to="/forgot-password" style={{ ...S.link, display: 'block', textAlign: 'center', fontSize: '0.9rem', fontWeight: 600 }}>
              לבקשת קישור חדש
            </Link>
          </>
        ) : done ? (
          <div role="status" style={{ ...S.successBox, marginBottom: 0 }}>
            הסיסמה עודכנה! עוברים למסך הכניסה…
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="new-password" style={S.label}>
                סיסמה חדשה
                <span style={{ fontWeight: 400, color: S.colors.muted, marginRight: 6, fontSize: '0.8rem' }}>
                  (לפחות 6 תווים)
                </span>
              </label>
              <PasswordInput
                id="new-password"
                name="new-password"
                value={password} onChange={e => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                style={S.input}
                {...S.inputFocusHandlers}
                {...S.validationHandlers}
              />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label htmlFor="confirm-password" style={S.label}>
                אישור סיסמה
              </label>
              <PasswordInput
                id="confirm-password"
                name="confirm-password"
                value={confirm} onChange={e => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
                style={S.input}
                {...S.inputFocusHandlers}
                {...S.validationHandlers}
              />
            </div>
            {error && (
              <div role="alert" style={{ ...S.errorBox, marginBottom: 14 }}>{error}</div>
            )}
            <button
              type="submit"
              disabled={saving}
              aria-busy={saving}
              style={S.button(saving)}
              {...S.buttonHoverHandlers}
            >
              {saving ? 'שומרים…' : 'עדכון סיסמה'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
